/**
 * Reflective Data additions (see RD_PATCHES.md). When Lightdash runs inside the RD app's iframe, the app shell
 * already provides the logo, project/account switching, user menu and help, so those are hidden here.
 *
 * This only declutters the UI. It is NOT access control: what a person can do is decided by the Lightdash role
 * and project access that our API provisions for them.
 */

export const isRdEmbedded = (() => {
    try {
        return window.self !== window.top;
    } catch {
        return true; // cross-origin access to window.top throws: that means we are framed
    }
})();

export type RdHideable =
    | 'logo'
    | 'projectSwitcher'
    | 'userMenu'
    | 'settings'
    | 'help'
    | 'whatsNew'
    | 'learn'
    | 'aiAgents'
    | 'autopilot'
    | 'footer'
    | 'writeBack'
    | 'notifications'
    | 'announcements'
    | 'homeGreeting'
    | 'login'
    | 'brandLoader';

/** UI pieces hidden when embedded. Add a name here to hide it; remove it to show it again. */
const HIDDEN_WHEN_EMBEDDED = new Set<RdHideable>([
    'logo',
    'projectSwitcher',
    'userMenu',
    'settings',
    'help',
    'whatsNew',
    'learn',
    'aiAgents',
    'autopilot',
    'footer',
    'notifications', // the bell: the RD app has its own place for news
    'announcements', // Lightdash's "Learn more" popovers, promo cards and feature announcements
    'brandLoader', // the loading mark is Reflective Data's, not Lightdash's bolt
    'login', // never show Lightdash's sign-in: the RD app signs people in, and the page asks it to reconnect instead
    'homeGreeting', // "Welcome, <name>" on the home page: it opens with the two ways to start instead
    'writeBack', // "Write back to project" opens pull requests in a git repo; RD projects have no git connection in Lightdash (the repo is managed in Build)
]);

export const rdHides = (feature: RdHideable) =>
    isRdEmbedded && HIDDEN_WHEN_EMBEDDED.has(feature);

/** The top bar is dark in upstream's design. Embedded in the RD app's light theme it follows the app and turns light. */
export const rdLightNavbar = (appColorScheme: 'light' | 'dark') =>
    isRdEmbedded && appColorScheme === 'light';

/** Embedded, people never see the product name: "Lightdash" in a message reads "Explore". */
export const rdWording = (message: string) =>
    isRdEmbedded ? message.replace(/Lightdash/g, 'Explore') : message;
